import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {Blueprint} from '../../models/blueprints/blueprint';
import {FabricService} from 'src/app/services/fabric/fabric.service';
import {toBoolean} from 'src/app/services/util/utils';
import {tap} from 'rxjs/operators';
import {PathToLiveTemplate} from 'src/app/models/blueprints/path-to-live-template';
import { BlueprintVersion } from 'src/app/models/blueprints/blueprint-version';

const SERVICE_NAME = 'mgmt-blueprint-resources';

@Injectable({
  providedIn: 'root'
})
export class BlueprintsService {

  constructor(private fabricService: FabricService) {

  }

  createBlueprint(blueprint: Blueprint): Observable<Blueprint> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_mgmt_blueprints', blueprint);
  }

  getBlueprint(id: string): Observable<Blueprint> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_blueprints_id', {id})
      .pipe(tap(blueprint => this.convertModel(blueprint)));
  }

  updateBlueprint(blueprint: Blueprint) {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_mgmt_blueprints_id', blueprint)
      .pipe(tap(blueprint => this.convertModel(blueprint)));
  }

  getBlueprints(tenantId?): Observable<Blueprint[]> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_blueprints', {tenantId})
      .pipe(tap(blueprints => blueprints.forEach(this.convertModel)));
  }

  getBlueprintsForSolution(solutionId?): Observable<Blueprint[]> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_blueprints', {solutionId})
      .pipe(tap(blueprints => blueprints.forEach(this.convertModel)));
  }

  getBlueprintsIdLatestVersion(id: string): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_blueprints_blueprintid_latest_version', {blueprintId: id})
      .pipe(tap(blueprint => this.convertModel(blueprint)));
  }

  getBlueprintVersion(id: string): Observable<BlueprintVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_blueprints_blueprintid_versions', {blueprintId: id});
  }

  getBlueprintVersionById(blueprintId: string, id: string): Observable<BlueprintVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_blueprints_blueprintid_versions_id', {blueprintId,id});
  }

  deleteBlueprint(id: string): any {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'delete_mgmt_blueprints_id', {id});
  }

  deleteBlueprintVersionById(blueprintId: string, id: string): Observable<BlueprintVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'delete_mgmt_blueprints_blueprintid_versions_id', {blueprintId,id});
  }

  getPathToLiveTemplates(): Observable<PathToLiveTemplate[]> {
    // Convert boolean variables since Fabric returns strings
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_blueprints_path-to-live-templates')
      .pipe(tap(pathToLiveTemplates => pathToLiveTemplates.forEach(pathToLiveTemplate => {
        pathToLiveTemplate.configuration.forEach(configuration => configuration.editable = toBoolean(configuration.editable));
      })));
  }

  createBlueprintVersion(id: string, tenantId, solutionVersionId?: any, capabilities?: any, installDocRaw?: any): Observable<BlueprintVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_mgmt_blueprints_blueprintid_versions', {blueprintId: id, tenantId, solutionVersionId, installDocRaw, capabilities});
  }

  /**
   * Workaround to convert model's boolean values since fabric returns them as strings
   *
   * @param blueprint model
   */
  private convertModel(blueprint: Blueprint) {
    blueprint.demo = toBoolean(blueprint.demo);
    if (blueprint.pathToLiveConfiguration) {
      blueprint.pathToLiveConfiguration.forEach(pathToLiveConfiguration => {
        pathToLiveConfiguration.editable = toBoolean(pathToLiveConfiguration.editable);
      });
    }
  }
}
